import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import { formatDate } from '../lib/format'
import type { Client } from '../types'

/**
 * Кабинет клиента глазами нашего менеджера.
 *
 * Менеджер делает ровно две вещи: назначает ответственного и решает, сколько
 * тому дать мест. Приглашать сотрудников и отключать уволившихся — дело
 * ответственного со стороны клиента: об увольнениях у него узнают в тот же
 * день, а нам не сообщают никогда.
 */
export default function PortalTab({
  clientId,
  client,
}: {
  clientId: number
  client: Client | null
}) {
  const qc = useQueryClient()
  const { can } = useAuth()
  const mayEdit = can('clients.edit')
  const [error, setError] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [seats, setSeats] = useState<string>('')

  const users = useQuery({
    queryKey: ['portal-users', clientId],
    queryFn: () => api.portalUsers(clientId),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['portal-users', clientId] })
    qc.invalidateQueries({ queryKey: ['clients'] })
  }

  const saveSeats = useMutation({
    mutationFn: (value: number) => api.setPortalSeats(clientId, value),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить'),
  })

  const setActive = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      api.setPortalUserActive(clientId, id, on),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const rows = users.data ?? []
  const admin = rows.find((u) => u.is_admin && u.is_active)
  const used = rows.filter((u) => !u.is_admin && u.is_active).length
  const total = client?.portal_seats ?? 3

  return (
    <>
      {error && <div className="error">{error}</div>}

      {!admin && (
        <div className="warn">
          У клиента нет ответственного за кабинет — войти туда сейчас некому.
          Пригласите его: дальше он сам заводит и отключает своих сотрудников.
        </div>
      )}

      <div className="toolbar">
        {mayEdit && (
          <button
            className="btn primary"
            onClick={() => {
              setError('')
              setInviteOpen(true)
            }}
          >
            {admin ? 'Сменить ответственного' : '+ Пригласить ответственного'}
          </button>
        )}
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="pt-seats">Мест для сотрудников</label>
          <input
            id="pt-seats"
            type="number"
            min="0"
            max="50"
            disabled={!mayEdit}
            value={seats === '' ? String(total) : seats}
            onChange={(e) => setSeats(e.target.value)}
            onBlur={() => {
              const value = Number(seats)
              if (seats !== '' && Number.isFinite(value) && value !== total) {
                saveSeats.mutate(value)
              }
              setSeats('')
            }}
          />
          <span className="hint-inline">Ответственный сверх этого числа</span>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>
          Занято: {used} из {total}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Кто</th>
              <th>Почта</th>
              <th>Должность</th>
              <th>Последний вход</th>
              <th>Доступ</th>
              {mayEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {users.isLoading && (
              <tr>
                <td colSpan={mayEdit ? 6 : 5} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!users.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={mayEdit ? 6 : 5} className="empty">
                  Кабинет ещё не открыт
                </td>
              </tr>
            )}
            {rows.map((u) => (
              <tr key={u.id} style={u.is_active ? undefined : { opacity: 0.6 }}>
                <td>
                  {u.full_name}
                  {u.is_admin && <span className="group-badge">ответственный</span>}
                </td>
                <td>{u.email}</td>
                <td>{u.position ?? '—'}</td>
                <td className="nowrap">
                  {u.last_login_at ? formatDate(u.last_login_at) : 'ни разу'}
                </td>
                <td>{u.is_active ? 'есть' : 'отключён'}</td>
                {mayEdit && (
                  <td className="nowrap">
                    <button
                      className="btn small"
                      disabled={setActive.isPending}
                      onClick={() => setActive.mutate({ id: u.id, on: !u.is_active })}
                    >
                      {u.is_active ? 'Отключить' : 'Включить'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hint" style={{ marginTop: 12 }}>
        Отключайте отсюда, только если клиент перестал обслуживаться. Уволившихся
        сотрудников убирает ответственный со стороны клиента — он один знает, кто
        ещё работает.
      </p>

      {inviteOpen && (
        <InviteAdminDialog
          clientId={clientId}
          hasAdmin={!!admin}
          onClose={() => setInviteOpen(false)}
          onDone={() => {
            setInviteOpen(false)
            refresh()
          }}
        />
      )}
    </>
  )
}

function InviteAdminDialog({
  clientId,
  hasAdmin,
  onClose,
  onDone,
}: {
  clientId: number
  hasAdmin: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({ email: '', full_name: '', position: '' })
  const [error, setError] = useState('')
  const [link, setLink] = useState('')
  const [mailed, setMailed] = useState(true)
  const [copied, setCopied] = useState(false)

  const invite = useMutation({
    mutationFn: () =>
      api.invitePortalAdmin(clientId, {
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        position: form.position.trim() || null,
      }),
    onSuccess: (res) => {
      setLink(res.link ?? '')
      setMailed(res.mailed)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось пригласить'),
  })

  const sent = !!link

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Браузер не дал доступ к буферу обмена — скопируйте вручную')
    }
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  const ready = form.email.trim() && form.full_name.trim()

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 460 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          invite.mutate()
        }}
      >
        <h2>{hasAdmin ? 'Сменить ответственного' : 'Пригласить ответственного'}</h2>
        <div className="modal-body">
          {sent ? (
            <>
              {mailed ? (
                <p>
                  Приглашение отправлено на {form.email}. Человек появится в
                  списке, когда войдёт по ссылке.
                </p>
              ) : (
                <div className="warn">
                  Письмо не ушло — почта не настроена или не приняла адрес.
                  Приглашение заведено: передайте ссылку сами, иначе войти будет
                  нечем.
                </div>
              )}
              <div className="field full field-accent">
                <label>Ссылка для входа</label>
                <input readOnly value={link} onFocus={(e) => e.target.select()} />
                <span className="hint-inline">
                  Одноразовая, действует семь дней. Второй раз ссылку не узнать —
                  в базе от неё только отпечаток.
                </span>
              </div>
              <button type="button" className="btn" onClick={() => void copy()}>
                {copied ? '✓ Скопировано' : 'Копировать ссылку'}
              </button>
            </>
          ) : (
            <>
              {error && <div className="error">{error}</div>}
              <p className="hint">
                Ответственный заводит и отключает сотрудников своей организации сам.
                Приглашайте того, кто действительно этим займётся — директора,
                главного бухгалтера, руководителя подразделения.
              </p>
              {hasAdmin && (
                <div className="warn">
                  Прежний ответственный останется в кабинете обычным сотрудником —
                  доступ он не потеряет, но распоряжаться больше не сможет.
                </div>
              )}
              <div className="field">
                <label htmlFor="pa-name">Фамилия и имя</label>
                <input id="pa-name" value={form.full_name} onChange={set('full_name')} required autoFocus />
              </div>
              <div className="field">
                <label htmlFor="pa-mail">Рабочая почта</label>
                <input id="pa-mail" type="email" value={form.email} onChange={set('email')} required />
              </div>
              <div className="field">
                <label htmlFor="pa-pos">Должность</label>
                <input id="pa-pos" value={form.position} onChange={set('position')} />
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={sent ? onDone : onClose}>
            {sent ? 'Закрыть' : 'Отмена'}
          </button>
          {!sent && (
            <button className="btn primary" disabled={invite.isPending || !ready}>
              {invite.isPending ? 'Отправляем…' : 'Отправить'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
